<template>
    <grid
        :fetchCallback="fetchCallback"
        :columnDefs="columnDefs"
        :suppressScrollOnNewData="true"
        :maxBlocksInCache="null"
        ref="grid"
        :headerHeight="45"
        :pinnedBottomRowData="pinnedBottomRowData"
        @on-total-records="setTotalRecords"
        :getRowStyle="rowStyle"
    >
        <date-range-picker
            :range-options="predefinedRanges"
            :value="dateRange"
            :date-format="userUnicodeDateFormat"
            @input="handleDaterangeChange"
            :disabled="disableDatepicker"
        ></date-range-picker>
        <b-button-group class="d-none d-xl-flex">
            <search-multiselect v-model="selectedProperties" :options="properties" :icon="['fas', 'hotel']" class="mr-1"
                variant="primary" placeholder="Properties" controlValue="Properties"></search-multiselect>
        </b-button-group>
        <responsive-dropdown id="reservations-filter-by-state" :items="filterByStatusOptions" :responsiveness="false"
            variant="primary" class="d-none d-xl-flex">
            <div class="d-flex align-item-center">
                <div class="mr-2" v-html="getReservationStatusIcon(filterByStatus.value)"></div>
                {{ filterByStatus.label }}
            </div>
        </responsive-dropdown>
        <b-button-group class="d-none d-xl-flex">
            <responsive-dropdown id="reservations-filter" :items="filterOptions" :responsiveness="false" variant="primary"
                class="d-none d-xl-flex mr-1" :icon="['fas', 'filter']">
                {{ filter.label }}
            </responsive-dropdown>
        </b-button-group>
        <b-button-group class="d-none d-xl-flex">
            <search-multiselect v-model="selectedColumns" :options="columnsOptions" class="mr-1" variant="primary"
                placeholder="Columns" controlValue="Columns"></search-multiselect>
        </b-button-group>

        <responsive-button variant="primary" @click="exportToExcel" :icon="['fas', 'file-excel']"></responsive-button>
        <responsive-button variant="primary" @click="refresh" :icon="['fas', 'arrows-rotate']">Refresh</responsive-button>
        <responsive-button variant="primary" @click="resetFilters"
            :icon="['fas', 'arrow-rotate-right']">Reset</responsive-button>
        <responsive-button variant="primary" href="/app/reservation/create" :icon="['fas', 'plus']"></responsive-button>
        <b-button-group class="d-xl-none">
            <more-filters-dropdown>
                <template v-slot:default="{ handleDropdownShow, handleDropdownHide }">
                    <search-multiselect id="reservations-filter-by-properties" :options="properties"
                        :icon="['fas', 'hotel']" v-model="selectedProperties" class="mr-1" variant="primary"
                        placeholder="Properties" controlValue="Properties" @show="handleDropdownShow"
                        @hide="handleDropdownHide"></search-multiselect>
                    <responsive-dropdown id="reservations-filter-by-state" :items="filterByStatusOptions"
                        :responsiveness="false" variant="primary" @show="handleDropdownShow" @hide="handleDropdownHide">
                        <div class="d-flex align-item-center">
                            <div class="mr-2" v-html="getReservationStatusIcon(filterByStatus.value)"></div>
                            {{ filterByStatus.label }}
                        </div>
                    </responsive-dropdown>
                    <responsive-dropdown id="reservations-filter" :items="filterOptions" :responsiveness="false"
                        variant="primary" class="reservation-filter mr-1" :icon="['fas', 'filter']"
                        @show="handleDropdownShow" @hide="handleDropdownHide">
                        {{ filter.label }}
                    </responsive-dropdown>
                    <search-multiselect id="reservations-filter-by-columns" :options="columnsOptions"
                        v-model="selectedColumns" class="mr-1" variant="primary" placeholder="Columns"
                        controlValue="Columns" @show="handleDropdownShow" @hide="handleDropdownHide"></search-multiselect>
                </template>
            </more-filters-dropdown>
        </b-button-group>
        <busy-modal :show="busy" />
    </grid>
</template>

<script>
import ActionsCellRenderer from './actions-cell-renderer.vue';
import API from '../../../api';
import BusyModal from '../../generic/busy-modal/busy-modal.vue';
import grid from '../../generic/grid/grid.vue';
import { escapeHTML } from '../../../utils/strings';
import DateRangePicker, {
    ALL_DATES,
    LAST_MONTH,
    LAST_YEAR,
    LAST_WEEK,
    THIS_WEEK,
    nameToRange,
    THIS_MONTH,
    THIS_YEAR,
    TODAY,
    YESTERDAY,
    FUTURE,
} from '../../generic/date-range-picker/date-range-picker.vue';
import {
    getReservationStatusIconStyle,
    RESERVATION_STATUS_PAID,
    RESERVATION_STATUS_PAIDDEPOSIT,
    RESERVATION_STATUS_NOTPAID,
    RESERVATION_STATUS_CANCELLED,
} from '../../../utils/reservation-state';
import format from 'date-fns/format';
import { mapGetters } from 'vuex';
import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
import {
    RESERVATION_GRID_ENDPOINT,
    RESERVATION_DELETE_ENDPOINT,
    REPORT_PROPERTIES_ALL,
} from '../../../endpoints';
import responsiveButton from '../../generic/responsive-button/responsive-button.vue';
import ReservationsStateCellRenderer from './reservations-state-cell-renderer.vue';
import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
import SearchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';
import ReservationIconsCellRenderer from './reservation-icons-cell-renderer.vue';
import { toAmountDisplay } from '../../../utils/numbers';

export default {
    components: {
        grid,
        responsiveButton,
        DateRangePicker,
        SearchMultiselect,
        ResponsiveDropdown,
        MoreFiltersDropdown,
        BusyModal,
        actionsCellRenderer: ActionsCellRenderer,
        reservationsStateCellRenderer: ReservationsStateCellRenderer,
        reservationIconsCellRenderer: ReservationIconsCellRenderer,
    },
    data() {
        return {
            busy: null,
            dateRange: nameToRange(FUTURE),
            dateRequestFormat: 'yyyy-MM-dd',
            desc: false,
            filter: {
                label: 'No Filters',
                value: '',
            },
            filterByStatus: {
                label: 'All Statuses',
                value: null,
            },
            limit: 20,
            offset: 0,
            predefinedRanges: [
                FUTURE,
                TODAY,
                YESTERDAY,
                THIS_WEEK,
                LAST_WEEK,
                THIS_MONTH,
                LAST_MONTH,
                THIS_YEAR,
                LAST_YEAR,
                ALL_DATES,
            ],
            properties: [],
            sort: 'created',
            search: '',
            selectedColumns: [
                'created',
                'from_date',
                'to_date',
                'properties',
                'first_name',
                'last_name',
                'zip_code',
                'total',
                'other_deposits',
                'total_with_deposits',
                'paid',
                'balance',
                'balance_due_date',
                'confirmation_amount',
            ],
            selectedProperties: [],
            totalRecords: 0,
            rowStyle: null,
        };
    },

    mounted() {
        this.fetchProperties();
        const filter = this.$route.hash?.split('/')[2];
        if (filter) {
            this.filter = this.getFilterOptionObject(filter);
        }
    },
    beforeMount() {
        this.rowStyle = (params) => {
            if (params.node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol']),
        ...mapGetters('reservationsStore', ['guestEdit', 'reservationEdit', 'reservationView']),
        excelHref() {
            return `/app/reservations-list/reservations/excel/?offset=${this.offset}&limit=${this.limit}&sort=${this.sort
                }&desc=${this.desc}&start_date=${this.startDateRequest}&end_date=${this.endDateRequest}&${this.selectedProperties && 'properties=' + this.selectedProperties.join(',')
                }&${this.filterByStatus.value && 'status=' + this.filterByStatus.value}&${this.filter.value && 'filter=' + this.filter.value
                }&search=${this.search}`;
        },
        pinnedBottomRowData() {
            return [{
                number: this.totalRecords,
            }];
        },
        reservationColumns() {
            return [
                {
                    field: 'created',
                    headerName: 'Created',
                    type: 'dateColumn',
                    minWidth: 120,
                    maxWidth: 120,
                    cellClass: 'pr-0 pl-2',
                    headerClass: 'pr-1 pl-2',
                },
                {
                    field: 'from_date',
                    headerName: 'From',
                    type: 'dateColumn',
                    minWidth: 100,
                    maxWidth: 100,
                    cellClass: 'pr-0 pl-2',
                    headerClass: 'pr-1 pl-2',
                },

                {
                    field: 'to_date',
                    headerName: 'To',
                    type: 'dateColumn',
                    minWidth: 100,
                    maxWidth: 100,
                    cellClass: 'pr-0 pl-2',
                    headerClass: 'pr-1 pl-2',
                },
                {
                    field: 'properties',
                    headerName: 'Property',
                    cellRenderer: ({ data, node }) => {
                        if (!node.rowPinned) {
                            return data.properties
                                .map((item) => {
                                    return escapeHTML(item.name);
                                })
                                .join('<br />');
                        }
                    },
                    cellStyle: { whiteSpace: 'normal' },

                    minWidth: 190,
                    autoHeight: true,
                },
                {
                    field: 'first_name',
                    headerName: 'First Name',
                    minWidth: 130,
                    cellRenderer: ({ data, node }) => {
                        if (!node.rowPinned) {
                            return this.guestEdit
                                ? `<a style="white-space: normal;"
                                    href="/app/guest/${data.user_id}/invoices/?reservation=${data.id}">${data.first_name}</a>`
                                : data.first_name;
                        }
                    },
                    cellStyle: { whiteSpace: 'normal' },
                },
                {
                    field: 'last_name',
                    headerName: 'Last Name',
                    minWidth: 125,
                    cellRenderer: ({ data, node }) => {
                        if (!node.rowPinned) {
                            return this.guestEdit
                                ? `<a href="/app/guest/${data.user_id}/invoices/?reservation=${data.id}">${data.last_name}</a>`
                                : data.last_name;
                        }
                    },
                },
                {
                    field: 'zip_code',
                    headerName: 'Guest Zip',
                    minWidth: 80,
                    maxWidth: 80,
                    cellClass: 'pr-0 pl-2',
                    headerClass: 'pr-1 pl-2',
                },
                {
                    field: 'total',
                    headerName: 'Charges',
                    minWidth: 93,
                    maxWidth: 110,
                    sortable: false,
                    cellRenderer: ({ data, node }) => {
                        if (!node.rowPinned) {
                            return toAmountDisplay({
                                amount: data.total,
                                currency: this.userCurrencySymbol,
                            });
                        }
                    },
                },
                {
                    field: 'other_deposits',
                    headerName: 'Other Deposits',
                    sortable: false,
                    minWidth: 93,
                    maxWidth: 110,
                    type: 'amountColumn',
                },
                {
                    field: 'total_with_deposits',
                    headerName: 'Total',
                    sortable: false,
                    minWidth: 93,
                    maxWidth: 110,
                    type: 'amountColumn',
                },
                {
                    field: 'paid',
                    headerName: 'Paid',
                    sortable: false,
                    minWidth: 93,
                    maxWidth: 110,
                    type: 'amountColumn',
                },
                {
                    field: 'balance',
                    headerName: 'Balance',
                    sortable: false,
                    minWidth: 93,
                    maxWidth: 110,
                    type: 'amountColumn',
                },
                {
                    field: 'balance_due_date',
                    headerName: 'Due',
                    type: 'dateColumn',
                    minWidth: 105,
                    maxWidth: 105,
                    cellClass: 'pr-0 pl-2',
                    headerClass: 'pr-1 pl-2',
                },
                {
                    field: 'confirmation_amount',
                    headerName: 'To Confirm',
                    sortable: false,
                    minWidth: 105,
                    maxWidth: 105,
                    type: 'amountColumn',
                },
            ].filter((item) => this.selectedColumns.find((i) => item.field === i));
        },
        columnDefs() {
            return [
                {
                    field: '',
                    headerName: '',
                    cellRenderer: 'reservationsStateCellRenderer',
                    cellRendererParams: {
                        totalRecords: this.totalRecords,
                    },
                    sortable: false,
                    minWidth: 35,
                    maxWidth: 35,
                    cellStyle: { paddingRight: 0, paddingLeft: '9px' },
                    resizable: false,
                },
                {
                    field: '',
                    headerName: '',
                    cellRenderer: 'reservationIconsCellRenderer',
                    sortable: false,
                    cellStyle: { paddingRight: 0, paddingLeft: 0 },
                    maxWidth: 90,
                },
                {
                    headerName: '#',
                    field: 'number',
                    minWidth: 60,
                    maxWidth: 80,
                    sortable: false,
                    valueGetter: (params) => {
                        return params.node.rowPinned ? this.totalRecords : params.node.rowIndex + 1;
                    },
                },
                {
                    field: 'id',
                    headerName: 'ID',
                    maxWidth: 100,
                    minWidth: 100,
                    sort: 'desc',
                    cellRenderer: ({ data, node }) => {
                        if (!node.rowPinned) {
                            const reservation = data;
                            if (reservation.status == 'BLOCKED' || this.reservationEdit) {
                                return `<a href="/app/reservation/edit/invoice/${reservation.id}">${reservation.id}</a>`;
                            }
                            return `${reservation.id}`;
                        }
                    },
                },
                ...this.reservationColumns,
                ...this.actionColumn,
            ];
        },
        actionColumn() {
            return [
                {
                    field: '',
                    headerName: '',
                    cellRenderer: 'actionsCellRenderer',
                    sortable: false,
                    minWidth: 110,
                    maxWidth: 130,
                    cellStyle: { paddingRight: '4px', paddingLeft: '4px', display: 'flex', justifyContent: 'flex-end' },
                    cellRendererParams: {
                        deleteReservation: async (password, id) => {
                            try {
                                const { data } = await API.post(RESERVATION_DELETE_ENDPOINT(id), { password });
                                this.successToast({
                                    text: 'Reservation has been deleted',
                                });
                                this.$bvModal.hide('modalId' + id);
                                this.refresh();
                            } catch (e) {
                                this.errorToast({
                                    text: 'Could not delete reservation',
                                });
                            }
                        },
                    },
                },
            ];
        },
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                return API.get(RESERVATION_GRID_ENDPOINT(), {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                        start_date: this.disableDatepicker ? '' : this.startDateRequest,
                        end_date: this.disableDatepicker ? '' : this.endDateRequest,
                        properties: this.selectedProperties.join(','),
                        status: this.filterByStatus.value,
                        filter: this.filter.value,
                    },
                });
            };
        },
        columnsOptions() {
            return [
                { value: 'created', text: 'Created' },
                { value: 'from_date', text: 'From Date' },
                { value: 'to_date', text: 'To Date' },
                { value: 'properties', text: 'Properties' },
                { value: 'first_name', text: 'First Name' },
                { value: 'last_name', text: 'Last Name' },
                { value: 'zip_code', text: 'ZIP' },
                { value: 'total', text: 'Charges' },
                { value: 'other_deposits', text: 'Other Deposits' },
                { value: 'total_with_deposits', text: 'Total' },
                { value: 'paid', text: 'Paid' },
                { value: 'balance', text: 'Balance' },
                { value: 'balance_due_date', text: 'Due' },
                { value: 'confirmation_amount', text: 'To Confirm' },
            ];
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        filterOptions() {
            return [
                '',
                'balances_due',
                'refund_due',
                'pending',
                'cancelled',
                'arrivals_today',
                'arrivals_tomorrow',
                'arrivals_this_week',
                'arrivals_next_week',
                'consolidated_arrivals',
                'departures_today',
                'departures_tomorrow',
                'departures_next_week',
                'consolidated_departures',
                'owner_stay',
                'guest_stay',
            ].map((item) => {
                const { label, value } = this.getFilterOptionObject(item);
                return {
                    label,
                    active: this.filter.value === value,
                    click: () => {
                        this.filter = {
                            label,
                            value,
                        };
                        this.refresh();
                    },
                };
            });
        },
        filterByStatusOptions() {
            const filterByPaidInFull = {
                label: 'Paid-in-Full',
                value: RESERVATION_STATUS_PAID,
            };
            const filterByUnconfirmed = {
                label: 'Unconfirmed',
                value: RESERVATION_STATUS_NOTPAID,
            };
            const filterByConfirmed = {
                label: 'Confirmed',
                value: RESERVATION_STATUS_PAIDDEPOSIT,
            };
            const filterByCancelled = {
                label: 'Cancelled',
                value: RESERVATION_STATUS_CANCELLED,
            };

            const allStatuses = {
                label: 'All Statuses',
                value: null,
            };

            return [
                {
                    label: allStatuses.label,
                    active: this.filterByStatus.value === allStatuses.value,
                    click: () => {
                        this.filterByStatus = allStatuses;
                        this.refresh();
                    },
                    dropDownItemClass: 'filter-by-status-dropdown-item',
                },
                {
                    customHTML: this.getReservationStatusIcon(filterByPaidInFull.value),
                    label: filterByPaidInFull.label,
                    active: this.filterByStatus.value === filterByPaidInFull.value,
                    click: () => {
                        this.filterByStatus = filterByPaidInFull;
                        this.refresh();
                    },
                    dropDownItemClass: 'filter-by-status-dropdown-item',
                },
                {
                    customHTML: this.getReservationStatusIcon(filterByUnconfirmed.value),
                    label: filterByUnconfirmed.label,
                    active: this.filterByStatus.value === filterByUnconfirmed.value,
                    click: () => {
                        this.filterByStatus = filterByUnconfirmed;
                        this.refresh();
                    },
                    dropDownItemClass: 'filter-by-status-dropdown-item',
                },
                {
                    customHTML: this.getReservationStatusIcon(filterByConfirmed.value),

                    label: filterByConfirmed.label,
                    active: this.filterByStatus.value === filterByConfirmed.value,
                    click: () => {
                        this.filterByStatus = filterByConfirmed;
                        this.refresh();
                    },
                    dropDownItemClass: 'filter-by-status-dropdown-item',
                },
                {
                    customHTML: this.getReservationStatusIcon(filterByCancelled.value),
                    label: filterByCancelled.label,
                    active: this.filterByStatus.value === filterByCancelled.value,
                    click: () => {
                        this.filterByStatus = filterByCancelled;
                        this.refresh();
                    },
                    dropDownItemClass: 'filter-by-status-dropdown-item',
                },
            ];
        },
        disableDatepicker() {
            return this.filter.value !== '';
        },
    },
    methods: {
        async exportToExcel() {
            this.generatingExcelReportSuccessToast();

            window.location = `/app/reservations-list/reservations/excel/?offset=${this.offset}&limit=${this.limit
                }&sort=${this.sort}&desc=${this.desc}&start_date=${this.disableDatepicker ? '' : this.startDateRequest
                }&end_date=${this.disableDatepicker ? '' : this.endDateRequest}&${this.selectedProperties && 'properties=' + this.selectedProperties.join(',')
                }&${this.filterByStatus.value && 'status=' + this.filterByStatus.value}&${this.filter.value && 'filter=' + this.filter.value
                }&search=${this.search}`;
        },
        getFilterOptionObject(filterName) {
            let label = null;
            switch (filterName) {
                case '':
                    label = 'No Filters';
                    break;
                case 'balances_due':
                    label = 'Balances Due';
                    break;
                case 'refund_due':
                    label = 'Refund Due';
                    break;
                case 'pending':
                    label = 'Pending';
                    break;
                case 'cancelled':
                    label = 'Cancelled';
                    break;
                case 'arrivals_today':
                    label = 'Arrivals Today';
                    break;
                case 'arrivals_tomorrow':
                    label = 'Arrivals Tomorrow';
                    break;
                case 'arrivals_this_week':
                    label = 'Arrivals This Week';
                    break;
                case 'arrivals_next_week':
                    label = 'Arrivals Next Week';
                    break;
                case 'consolidated_arrivals':
                    label = 'Consolidated Arrivals';
                    break;
                case 'departures_today':
                    label = 'Departures Today';
                    break;
                case 'departures_tomorrow':
                    label = 'Departures Tomorrow';
                    break;
                case 'departures_next_week':
                    label = 'Departures Next Week';
                    break;
                case 'consolidated_departures':
                    label = 'Consolidated Departures';
                    break;
                case 'owner_stay':
                    label = 'Owner Stay';
                    break;
                case 'guest_stay':
                    label = 'Guests Stay';
                    break;
            }
            return { label, value: filterName };
        },
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.refresh();
        },
        async fetchProperties() {
            try {
                const { data } = await API.get(REPORT_PROPERTIES_ALL());
                this.properties = data.map((item) => ({
                    value: item.id,
                    text: item.name,
                    disabled: item.disabled,
                }));
            } catch (e) {
                this.errorToast({ text: 'Could not load properties' });
            }
        },
        getSelectedColumns(columnName) {
            switch (columnName) {
                case 'id':
                    return;
            }
        },
        getReservationStatusIcon(status) {
            if (status) {
                const border = getReservationStatusIconStyle(status).border
                    ? `border: ${getReservationStatusIconStyle(status).border};`
                    : '';
                const background = getReservationStatusIconStyle(status).background
                    ? `background: ${getReservationStatusIconStyle(status).background}`
                    : '';
                return `<div class="filter-reservation-status-marker" style="${border}${background}"></div>`;
            }
            return '';
        },
        resetFilters() {
            this.filter = {
                label: 'No Filters',
                value: '',
            };
            this.filterByStatus = {
                label: 'All Statuses',
                value: null,
            };
            this.selectedColumns = [
                'created',
                'from_date',
                'to_date',
                'properties',
                'first_name',
                'last_name',
                'zip_code',
                'total',
                'other_deposits',
                'total_with_deposits',
                'paid',
                'balance',
                'balance_due_date',
                'confirmation_amount',
            ];
            this.selectedProperties = [];
        },
        setTotalRecords(value) {
            this.totalRecords = value;
        },
    },
    watch: {
        selectedProperties() {
            this.refresh();
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

@include media-breakpoint-down(xs) {
    .reservation-filter {
        :deep(.dropdown-menu) {
            height: 300px;
            overflow-y: auto;
        }
    }
}
</style>